import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Calendar, ArrowRight, UserPlus, Package, Clock, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useCart } from "../contexts/CartContext";
import { trackEvent, gtagConversion } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  return localStorage.getItem("mahra_session_id") || "";
}

function formatSlotTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
}

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");
  const cartGroup = searchParams.get("cart_group");
  const [status, setStatus] = useState("loading");
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { customer } = useCustomerAuth();
  const { fetchCartCount } = useCart();
  const [returnLabelConfig, setReturnLabelConfig] = useState(null);
  const [appointments, setAppointments] = useState([]);

  useEffect(() => { if (sessionId) pollPaymentStatus(); }, [sessionId]);

  useEffect(() => {
    axios.get(`${API}/settings/return-label`).then(r => setReturnLabelConfig(r.data)).catch(() => {});
  }, []);

  const pollPaymentStatus = async () => {
    if (attempts >= 10) { setStatus("failed"); return; }
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        setStatus("success");
        trackEvent("booking_completed", bookingId || "");
        try {
          await axios.post(`${API}/cart/complete-payment`, {}, { headers: { "x-session-id": getSessionId() } });
          localStorage.removeItem("mahra_checkout_state");
          fetchCartCount();
        } catch {}
        if (bookingId) {
          const br = await axios.get(`${API}/bookings/${bookingId}`);
          setBooking(br.data);
          // Fire Google Ads conversion — once per booking
          gtagConversion(bookingId, br.data.total_amount || 0);
          // Microsoft Clarity — booking complete event
          if (window.clarity) {
            window.clarity("set", "booking_value", String(br.data.total_amount || 0));
            window.clarity("set", "booking_outfit", br.data.outfit_name || "");
            window.clarity("set", "booking_duration", String(br.data.num_days || br.data.rental_days || ""));
            window.clarity("event", "booking_complete");
          }
        }
        // Fetch auto-booked appointments
        const apptQuery = cartGroup ? `cart_group_id=${cartGroup}` : bookingId ? `booking_id=${bookingId}` : "";
        if (apptQuery) {
          try {
            const apptRes = await axios.get(`${API}/appointments/my?${apptQuery}`);
            setAppointments(apptRes.data.appointments || []);
          } catch {}
        }
        return;
      }
      setAttempts(p => p + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch { setAttempts(p => p + 1); setTimeout(pollPaymentStatus, 2000); }
  };

  useEffect(() => {
    if (status === "success") {
      try {
        const stored = sessionStorage.getItem("bookingData");
        if (stored) {
          const bd = JSON.parse(stored);
          if (bd.bundleItems?.length > 0) {
            axios.post(`${API}/bundle-analytics/record`, {
              main_outfit_id: bd.outfitId,
              bundle_item_ids: bd.bundleItems.map(b => b.id),
            }).catch(() => {});
          }
        }
      } catch {}
      sessionStorage.removeItem("bookingData");
      sessionStorage.removeItem("mahra_build_look");
      sessionStorage.removeItem("mahra_build_look_rented");
    }
  }, [status]);

  const pickupAppt = appointments.find(a => a.appointment_type === "pickup");
  const dropoffAppt = appointments.find(a => a.appointment_type === "dropoff");

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative">
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      <Navbar />
      
      <div className="px-6 py-20 lg:py-28">
        <div className="max-w-2xl mx-auto text-center">
          {status === "loading" && (
            <div className="animate-fadeIn">
              <div className="w-20 h-20 border border-[#9A7B4F] flex items-center justify-center mx-auto mb-8"><Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" /></div>
              <h1 className="text-3xl text-[#676664] mb-4">Processing Your Booking</h1>
              <p className="text-[#676664]/50 font-light">Please wait while we confirm your reservation...</p>
            </div>
          )}
          {status === "success" && (
            <div className="animate-fadeIn">
              <div className="w-20 h-20 bg-[#D4B8B4] flex items-center justify-center mx-auto mb-8"><CheckCircle2 className="w-10 h-10 text-white" strokeWidth={1.5} /></div>
              <p className="text-[#9A7B4F] text-[11px] tracking-[0.3em] uppercase mb-4">Confirmed</p>
              <h1 className="text-3xl lg:text-4xl text-[#676664] mb-4">Booking Complete</h1>
              <p className="text-[#676664]/50 mb-10 font-light">Thank you for choosing Mahra. Confirmation sent to your email.</p>
              <p className="text-[11px] text-[#9A7B4F]/70 mb-8 font-light">Can't find the email? Please check your spam or junk folder.</p>
              {booking && (
                <div className="bg-white p-8 border border-[#282828]/10 text-left mb-10">
                  <h2 className="text-sm text-[#676664] mb-6 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />Reservation Details</h2>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Piece</span><span className="text-[#676664]">{booking.outfit_name}</span></div>
                    <div className="flex justify-between py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Confirmation</span><span className="font-mono text-[#676664]">{booking.id.slice(0, 8).toUpperCase()}</span></div>
                    <div className="flex justify-between py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Duration</span><span className="text-[#676664]">{booking.num_days} days</span></div>
                    <div className="flex justify-between py-3"><span className="text-[#676664]">Total Paid</span><span className="text-lg text-[#9A7B4F]">${booking.total_amount.toFixed(2)}</span></div>
                  </div>
                </div>
              )}

              {/* Section 5 — pickup-only banner for same/next-day event dates */}
              {booking?.event_date && booking?.delivery_method === "pickup" && (() => {
                try {
                  const [y, m, d] = String(booking.event_date).slice(0, 10).split("-").map(Number);
                  const nowMel = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
                  const today = new Date(nowMel.getFullYear(), nowMel.getMonth(), nowMel.getDate());
                  const ev = new Date(y, m - 1, d);
                  const delta = Math.round((ev - today) / (1000 * 60 * 60 * 24));
                  if (delta < 0 || delta > 1) return null;
                  return (
                    <div className="bg-[#FAF3E0] border border-[#E7CE9A] p-4 mb-6 text-left" data-testid="booking-success-pickup-only-banner">
                      <p className="text-[11px] text-[#3D2B1F] leading-relaxed">
                        <strong>Pickup only:</strong> Same-day and next-day bookings are collected in-store. We&apos;ll see you at the studio.
                      </p>
                    </div>
                  );
                } catch { return null; }
              })()}

              {/* Confirmed Appointments */}
              {(pickupAppt || dropoffAppt) && (
                <div className="bg-white border border-[#282828]/10 p-6 mb-6 text-left" data-testid="confirmed-appointments">
                  <h3 className="text-sm text-[#676664] mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                    Your Scheduled Appointments
                  </h3>
                  <div className="space-y-3">
                    {pickupAppt && (
                      <div className="flex items-center justify-between py-3 border-b border-[#282828]/5" data-testid="pickup-appointment-info">
                        <div>
                          <p className="text-[10px] text-[#676664]/50 tracking-[0.1em] uppercase">Collection / Delivery</p>
                          <p className="text-[13px] text-[#282828] mt-0.5">{pickupAppt.date} at {formatSlotTime(pickupAppt.time)}</p>
                        </div>
                        <span className="text-[9px] tracking-[0.1em] uppercase px-2 py-0.5 bg-[#4A7C59]/10 text-[#4A7C59]">Confirmed</span>
                      </div>
                    )}
                    {dropoffAppt && (
                      <div className="flex items-center justify-between py-3" data-testid="dropoff-appointment-info">
                        <div>
                          <p className="text-[10px] text-[#676664]/50 tracking-[0.1em] uppercase">Return / Drop-off</p>
                          <p className="text-[13px] text-[#282828] mt-0.5">{dropoffAppt.date} at {formatSlotTime(dropoffAppt.time)}</p>
                        </div>
                        <span className="text-[9px] tracking-[0.1em] uppercase px-2 py-0.5 bg-[#4A7C59]/10 text-[#4A7C59]">Confirmed</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#282828]/5">
                    <p className="text-[11px] text-[#676664]/60 leading-relaxed">
                      You can reschedule your appointments from your account up to 2 days before your booking ends.
                      After the cutoff, please <a href="mailto:hello@shopmahra.com.au" className="text-[#9A7B4F] underline">contact us</a> to make changes.
                    </p>
                  </div>
                </div>
              )}

              {/* Return Label Reminder - admin controlled */}
              {returnLabelConfig?.enabled && (
                <div className="bg-[#B8C4B8]/10 border border-[#B8C4B8]/20 p-4 mb-6" data-testid="return-reminder">
                  <div className="flex items-start gap-3">
                    <Package className="w-5 h-5 text-[#6B8F71] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="text-[13px] text-[#282828] font-medium mb-1">{returnLabelConfig.title}</p>
                      <p className="text-[11px] text-[#676664]/60 leading-relaxed">{returnLabelConfig.description}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {customer && (
                  <Link to="/account/bookings">
                    <Button className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-10 py-6 text-[11px] tracking-[0.15em] uppercase" data-testid="view-bookings-btn">
                      View My Bookings<ArrowRight className="ml-3 w-4 h-4" />
                    </Button>
                  </Link>
                )}
                <Link to="/catalog"><Button variant={customer ? "outline" : "default"} className={customer ? "border border-[#282828]/20 text-[#676664] hover:bg-[#6B5B4F]/5 px-10 py-6 text-[11px] tracking-[0.15em] uppercase" : "bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-10 py-6 text-[11px] tracking-[0.15em] uppercase"} data-testid="continue-browsing-btn">Continue Browsing</Button></Link>
              </div>
              {/* Create Account Prompt */}
              {!customer && (
                <div className="mt-10 p-6 bg-white border border-[#282828]/8 text-center" data-testid="create-account-prompt">
                  <UserPlus className="w-5 h-5 text-[#9A7B4F] mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-[13px] text-[#282828] mb-1">Save your details for next time?</p>
                  <p className="text-[11px] text-[#676664]/50 font-light mb-4">Create an account to track bookings, reschedule appointments, and manage returns.</p>
                  <Link to="/login">
                    <Button variant="outline" className="border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/5 text-[11px] tracking-[0.12em] uppercase px-8" data-testid="create-account-btn">
                      Create Account
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
          {status === "failed" && (
            <div className="animate-fadeIn">
              <div className="w-20 h-20 bg-red-100 flex items-center justify-center mx-auto mb-8"><XCircle className="w-10 h-10 text-red-400" strokeWidth={1.5} /></div>
              <h1 className="text-3xl text-[#676664] mb-4">Verification Failed</h1>
              <p className="text-[#676664]/50 mb-10 font-light">We couldn't verify your payment. Please contact us.</p>
              <Link to="/catalog"><Button className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-10 py-6 text-[11px] tracking-[0.15em] uppercase">Return to Collection</Button></Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
